import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Scissors, Calendar, Bell, CreditCard, CheckCircle, ChevronRight, Star, Users, LogIn, Menu, X, Clock, Smartphone, BarChart3, Eye } from 'lucide-react';
import api, { createBarbershop } from '../utils/api';

function generateSlugPreview(value) {
  return String(value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/(^-|-$)/g, '');
}

export default function LandingPage() {
  const navigate = useNavigate();
  const [showForm, setShowForm] = useState(false);
  const [showDemo, setShowDemo] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [nameSuggestions, setNameSuggestions] = useState([]);
  const [formData, setFormData] = useState({
    nome: '',
    telefone: '',
    slug: '',
    username: '',
    senha: '',
  });
  const [slugStatus, setSlugStatus] = useState(null);

  const checkSlug = async (slug) => {
    if (!slug || slug.length < 3) {
      setSlugStatus({ available: false, message: 'Use pelo menos 3 caracteres.' });
      return;
    }

    try {
      const response = await api.get('/barbershops/slug/check', { params: { slug } });
      setSlugStatus({
        available: response.data.available,
        message: response.data.available ? 'Link publico disponivel.' : 'Este link publico ja esta em uso.'
      });
    } catch {
      setSlugStatus(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setNameSuggestions([]);

    try {
      const payload = {
        nome: formData.nome,
        telefone: formData.telefone,
        username: formData.username,
        senha: formData.senha
      };
      const response = await createBarbershop(payload);
      const { barbershop } = response.data;
      
      // Redirecionar para o dashboard com login automático
      navigate(`/login?slug=${barbershop.username || formData.username}`);
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao criar barbearia');
      setNameSuggestions(err.response?.data?.suggestions || []);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-dark-900">
      {/* Navbar */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary-600 to-purple-600 flex items-center justify-center">
                <Scissors className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-bold text-white">Barber<span className="text-primary-400">SaaS</span></span>
            </div>
            
            {/* Desktop Menu */}
            <div className="hidden md:flex items-center gap-6">
              <a href="#recursos" className="text-gray-400 hover:text-white transition-colors">Recursos</a>
              <a href="#precos" className="text-gray-400 hover:text-white transition-colors">Preços</a>
              <Link 
                to="/login" 
                className="flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-600 text-gray-300 hover:border-gray-500 hover:text-white transition-all text-sm"
              >
                <LogIn className="w-4 h-4" />
                Login
              </Link>
              <button 
                onClick={() => setShowForm(true)}
                className="btn-primary text-sm"
              >
                Começar Agora
              </button>
            </div>

            {/* Mobile Menu Button */}
            <div className="md:hidden flex items-center gap-3">
              <Link 
                to="/login" 
                className="flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-600 text-gray-300 hover:border-gray-500 hover:text-white transition-all text-sm"
              >
                <LogIn className="w-4 h-4" />
                <span className="hidden sm:inline">Login</span>
              </Link>
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-gray-800 transition-colors"
              >
                {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
            </div>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden border-t border-dark-700">
              <div className="px-4 py-4 space-y-3">
                <a 
                  href="#recursos" 
                  className="block text-gray-400 hover:text-white transition-colors py-2"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Recursos
                </a>
                <a 
                  href="#precos" 
                  className="block text-gray-400 hover:text-white transition-colors py-2"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Preços
                </a>
                <button 
                  onClick={() => {
                    setShowForm(true);
                    setMobileMenuOpen(false);
                  }}
                  className="w-full btn-primary text-sm"
                >
                  Começar Agora
                </button>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="text-center lg:text-left">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary-500/10 border border-primary-500/20 mb-6">
                <Star className="w-4 h-4 text-primary-400 fill-primary-400" />
                <span className="text-sm text-primary-300">Sistema #1 para barbearias</span>
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
                Organize sua{' '}
                <span className="gradient-text">barbearia</span>
                <br />
                e nunca perca clientes
              </h1>
              
              <p className="text-lg text-gray-400 mb-8 max-w-xl mx-auto lg:mx-0">
                Sistema completo de agendamento com notificações WhatsApp, 
                cobrança automática e painel administrativo moderno.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <button 
                  onClick={() => setShowForm(true)}
                  className="btn-primary text-lg px-8"
                >
                  Começar Grátis
                  <ChevronRight className="w-5 h-5 ml-2" />
                </button>
                <button type="button" onClick={() => setShowDemo(true)} className="btn-outline text-lg">
                  Ver Demonstração
                </button>
              </div>

              <div className="mt-8 flex items-center gap-4 justify-center lg:justify-start text-sm text-gray-500">
                <div className="flex -space-x-2">
                  {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="w-8 h-8 rounded-full bg-gradient-to-br from-primary-600 to-purple-600 border-2 border-dark-900 flex items-center justify-center text-xs text-white font-medium">
                      {String.fromCharCode(64 + i)}
                    </div>
                  ))}
                </div>
                <span>+500 barbearias confiam no BarberSaaS</span>
              </div>
            </div>

            {/* Form Card */}
            <div className="relative">
              <div className="absolute -inset-1 bg-gradient-to-r from-primary-600 to-purple-600 rounded-3xl blur opacity-30"></div>
              <div className="relative card p-8">
                {showForm ? (
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <h3 className="text-2xl font-bold text-white mb-2">Crie sua conta</h3>
                    <p className="text-gray-400 mb-6">7 dias grátis. Sem cartão de crédito.</p>
                    
                  {error && (
                    <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
                      {error}
                      {nameSuggestions.length > 0 && (
                        <div className="mt-3">
                          <p className="text-red-300 mb-2">Sugestões disponíveis:</p>
                          <div className="flex flex-wrap gap-2">
                            {nameSuggestions.map((suggestion) => (
                              <button
                                key={suggestion}
                                type="button"
                                onClick={() => {
                                  setFormData(prev => ({
                                    ...prev,
                                    nome: suggestion,
                                    slug: generateSlugPreview(suggestion)
                                  }));
                                  setError('');
                                  setNameSuggestions([]);
                                }}
                                className="px-3 py-1 rounded-lg bg-red-500/10 border border-red-500/30 text-red-100 hover:bg-red-500/20"
                              >
                                {suggestion}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                    <div>
                      <label className="label">Nome publico da barbearia</label>
                      <input
                        type="text"
                        required
                        placeholder="Ex: Imperio Barber"
                        className="input"
                        value={formData.nome}
                        onChange={(e) => {
                          const nome = e.target.value;
                          setFormData(prev => ({
                            ...prev,
                            nome,
                            slug: generateSlugPreview(nome)
                          }));
                        }}
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Esse nome aparece no dashboard e na pagina publica.
                      </p>
                    </div>

                    <div>
                      <label className="label">Usuario para entrar no dashboard</label>
                      <input
                        type="text"
                        required
                        placeholder="ex: dono-imperio"
                        className="input"
                        value={formData.username}
                        onChange={(e) => setFormData({ ...formData, username: generateSlugPreview(e.target.value) })}
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Esse e o nome que voce usa no login do painel.
                      </p>
                    </div>

                    <div>
                      <label className="label">WhatsApp</label>
                      <input
                        type="tel"
                        required
                        placeholder="(11) 99999-9999"
                        className="input"
                        value={formData.telefone}
                        onChange={(e) => setFormData({...formData, telefone: e.target.value})}
                      />
                    </div>

                    <div>
                      <label className="label">Senha de Acesso</label>
                      <input
                        type="password"
                        required
                        placeholder="Crie uma senha segura"
                        className="input"
                        value={formData.senha}
                        onChange={(e) => setFormData({...formData, senha: e.target.value})}
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={loading}
                      className="btn-primary w-full mt-6"
                    >
                      {loading ? 'Criando...' : 'Criar Barbearia'}
                    </button>

                    <button
                      type="button"
                      onClick={() => setShowForm(false)}
                      className="btn-ghost w-full text-sm"
                    >
                      Voltar
                    </button>
                  </form>
                ) : (
                  <div className="text-center py-8">
                    <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-primary-600 to-purple-600 flex items-center justify-center mx-auto mb-6">
                      <Calendar className="w-10 h-10 text-white" />
                    </div>
                    <h3 className="text-2xl font-bold text-white mb-2">Comece agora mesmo</h3>
                    <p className="text-gray-400 mb-6">
                      Cadastre sua barbearia e comece a receber agendamentos em minutos.
                    </p>
                    <ul className="text-left space-y-3 mb-8">
                      {[
                        'Página de agendamento personalizada',
                        'Notificações WhatsApp automáticas',
                        'Dashboard completo',
                        '7 dias grátis para testar'
                      ].map((item, i) => (
                        <li key={i} className="flex items-center gap-3 text-gray-300">
                          <CheckCircle className="w-5 h-5 text-green-400" />
                          {item}
                        </li>
                      ))}
                    </ul>
                    <button 
                      onClick={() => setShowForm(true)}
                      className="btn-primary w-full"
                    >
                      Criar Conta Grátis
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="recursos" className="py-20 px-4 sm:px-6 lg:px-8 bg-dark-800/50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Tudo que você precisa em um só lugar
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Nosso sistema foi desenvolvido especificamente para barbearias,
              com foco em simplicidade e eficiência.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: Calendar,
                title: 'Agendamento Online',
                description: 'Clientes agendam 24h sem ligar ou mandar mensagem. Escolhem o melhor horário.',
                color: 'from-primary-600 to-purple-600'
              },
              {
                icon: Bell,
                title: 'Notificações WhatsApp',
                description: 'Você recebe notificação instantânea de cada novo agendamento no WhatsApp.',
                color: 'from-green-600 to-emerald-600'
              },
              {
                icon: Users,
                title: 'Gestão de Clientes',
                description: 'Veja histórico de agendamentos e mantenha relacionamento com seus clientes.',
                color: 'from-blue-600 to-cyan-600'
              },
              {
                icon: CreditCard,
                title: 'Cobrança Automática',
                description: 'Sistema de assinatura mensal com renovação automática e lembretes.',
                color: 'from-yellow-600 to-orange-600'
              },
              {
                icon: Scissors,
                title: 'Serviços Personalizados',
                description: 'Cadastre seus serviços com preços, duração e cores personalizadas.',
                color: 'from-pink-600 to-rose-600'
              },
              {
                icon: CheckCircle,
                title: 'Confirmação e Cancelamento',
                description: 'Controle total sobre agendamentos com confirmação e cancelamento fácil.',
                color: 'from-indigo-600 to-violet-600'
              }
            ].map((feature, i) => (
              <div key={i} className="card card-hover p-6 group">
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${feature.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                  <feature.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">{feature.title}</h3>
                <p className="text-gray-400">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="precos" className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Planos simples e transparentes
            </h2>
            <p className="text-gray-400">Comece grátis e escolha o plano ideal para sua barbearia.</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {[
              {
                key: 'basico',
                name: 'Básico',
                price: 'R$ 29,90',
                period: '/mês',
                description: 'Ideal para começar',
                features: [
                  'Até 100 agendamentos/mês',
                  'Página de agendamento',
                  'Notificações WhatsApp',
                  'Dashboard simples',
                  'Suporte por email'
                ],
                highlighted: false
              },
              {
                key: 'pro',
                name: 'Pro',
                price: 'R$ 59,90',
                period: '/mês',
                description: 'Para barbearias em crescimento',
                features: [
                  'Agendamentos ilimitados',
                  'Página personalizada',
                  'Notificações WhatsApp',
                  'Dashboard completo',
                  'Múltiplos barbeiros',
                  'Suporte prioritário',
                  'Relatórios avançados'
                ],
                highlighted: true
              },
              {
                key: 'premium',
                name: 'Premium',
                price: 'R$ 99,90',
                period: '/mês',
                description: 'Para múltiplas unidades',
                features: [
                  'Tudo do Pro',
                  'Múltiplas unidades',
                  'API de integração',
                  'Backup automático',
                  'Suporte 24/7',
                  'Treinamento incluído'
                ],
                highlighted: false
              }
            ].map((plan, i) => (
              <div 
                key={i} 
                className={`card p-8 relative ${plan.highlighted ? 'border-primary-500 shadow-neon-purple' : ''}`}
              >
                {plan.highlighted && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <span className="badge badge-info">Mais Popular</span>
                  </div>
                )}
                
                <div className="text-center mb-6">
                  <h3 className="text-xl font-semibold text-white mb-2">{plan.name}</h3>
                  <p className="text-gray-400 text-sm mb-4">{plan.description}</p>
                  <div className="flex items-baseline justify-center gap-1">
                    <span className="text-4xl font-bold text-white">{plan.price}</span>
                    <span className="text-gray-500">{plan.period}</span>
                  </div>
                </div>

                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, fi) => (
                    <li key={fi} className="flex items-center gap-3 text-gray-300 text-sm">
                      <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>

                <button 
                  onClick={() => navigate(`/checkout/${plan.key}`)}
                  className={`w-full ${plan.highlighted ? 'btn-primary' : 'btn-outline'}`}
                >
                  Começar Agora
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {showDemo && (
        <div className="fixed inset-0 z-[80] bg-black/80 backdrop-blur-sm px-4 py-6 overflow-y-auto">
          <div className="max-w-6xl mx-auto">
            <div className="card border-primary-500/30 overflow-hidden">
              <div className="p-5 border-b border-dark-600 flex items-center justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2 text-primary-300 text-sm mb-1">
                    <Eye className="w-4 h-4" />
                    Demonstracao visual
                  </div>
                  <h2 className="text-xl sm:text-2xl font-bold text-white">Como sua barbearia pode aparecer no BarberFlow</h2>
                  <p className="text-gray-400 text-sm mt-1">Dados ficticios. Essa tela nao cria agendamentos nem altera sua conta.</p>
                </div>
                <button onClick={() => setShowDemo(false)} className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-dark-700">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="grid lg:grid-cols-[1.15fr_0.85fr]">
                <div className="p-5 sm:p-7 bg-dark-900/60">
                  <div className="rounded-2xl overflow-hidden border border-dark-600 bg-dark-800 shadow-2xl">
                    <div className="relative h-44 sm:h-56 bg-[url('https://images.unsplash.com/photo-1585747860715-2ba37e788b70?w=1200&auto=format&fit=crop')] bg-cover bg-center">
                      <div className="absolute inset-0 bg-gradient-to-t from-dark-900 via-dark-900/50 to-transparent" />
                      <div className="absolute left-5 bottom-5">
                        <div className="flex items-center gap-3">
                          <div className="w-14 h-14 rounded-xl bg-primary-600 flex items-center justify-center border border-white/10">
                            <Scissors className="w-7 h-7 text-white" />
                          </div>
                          <div>
                            <h3 className="text-2xl font-bold text-white">Barbearia Prime</h3>
                            <p className="text-sm text-gray-300">Cortes premium, barba e experiencia completa</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="p-5 space-y-5">
                      <div>
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="text-white font-semibold">Escolha o servico</h4>
                          <span className="badge-info">Pagina publica</span>
                        </div>
                        <div className="grid sm:grid-cols-3 gap-3">
                          {[
                            ['Corte Degrade', 'R$ 35', '30 min'],
                            ['Barba Premium', 'R$ 25', '20 min'],
                            ['Corte + Barba', 'R$ 55', '50 min']
                          ].map(([name, price, time]) => (
                            <div key={name} className="rounded-xl border border-primary-500/25 bg-primary-500/10 p-4">
                              <div className="w-10 h-10 rounded-lg bg-primary-600/80 flex items-center justify-center mb-3">
                                <Scissors className="w-5 h-5 text-white" />
                              </div>
                              <p className="text-white font-medium">{name}</p>
                              <div className="flex items-center justify-between mt-2 text-sm">
                                <span className="text-primary-300">{price}</span>
                                <span className="text-gray-400">{time}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className="grid sm:grid-cols-2 gap-3">
                        <div className="rounded-xl bg-dark-700 border border-dark-600 p-4">
                          <div className="flex items-center gap-2 text-gray-300 mb-3">
                            <Calendar className="w-4 h-4 text-primary-400" />
                            Horarios disponiveis
                          </div>
                          <div className="grid grid-cols-3 gap-2">
                            {['09:00', '10:30', '14:00', '15:30', '17:00', '18:30'].map(time => (
                              <span key={time} className="text-center rounded-lg bg-dark-600 text-gray-200 py-2 text-sm">{time}</span>
                            ))}
                          </div>
                        </div>
                        <div className="rounded-xl bg-dark-700 border border-dark-600 p-4">
                          <div className="flex items-center gap-2 text-gray-300 mb-3">
                            <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                            Avaliacoes
                          </div>
                          <p className="text-white font-medium">4.9 de 5.0</p>
                          <p className="text-sm text-gray-400 mt-2">"Atendimento rapido, agenda clara e confirmacao pelo WhatsApp."</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="p-5 sm:p-7 bg-dark-800">
                  <div className="space-y-4">
                    <div>
                      <h3 className="text-white text-lg font-semibold">Painel administrativo</h3>
                      <p className="text-gray-400 text-sm">Visao resumida do que o dono acompanha no dia a dia.</p>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      {[
                        ['Agendamentos hoje', '18', Calendar],
                        ['Faturamento', 'R$ 640', CreditCard],
                        ['Clientes avisados', '32', Smartphone],
                        ['Taxa de presenca', '94%', BarChart3]
                      ].map(([label, value, Icon]) => (
                        <div key={label} className="rounded-xl border border-dark-600 bg-dark-700 p-4">
                          <Icon className="w-5 h-5 text-primary-400 mb-3" />
                          <p className="text-xl font-bold text-white">{value}</p>
                          <p className="text-xs text-gray-400 mt-1">{label}</p>
                        </div>
                      ))}
                    </div>

                    <div className="rounded-xl border border-dark-600 bg-dark-700 p-4">
                      <div className="flex items-center justify-between mb-4">
                        <p className="text-white font-medium">Fila de atendimentos</p>
                        <span className="text-xs text-green-300 bg-green-500/10 border border-green-500/20 rounded-full px-2 py-1">Ao vivo</span>
                      </div>
                      <div className="space-y-3">
                        {[
                          ['10:30', 'Lucas Almeida', 'Corte Degrade', 'Confirmado'],
                          ['11:00', 'Rafael Souza', 'Barba Premium', 'Chegou'],
                          ['14:00', 'Mateus Lima', 'Corte + Barba', 'Aguardando']
                        ].map(([time, client, service, status]) => (
                          <div key={`${time}-${client}`} className="flex items-center gap-3 rounded-lg bg-dark-800 p-3">
                            <div className="w-12 text-sm text-primary-300 font-medium">{time}</div>
                            <div className="flex-1 min-w-0">
                              <p className="text-white text-sm font-medium truncate">{client}</p>
                              <p className="text-gray-400 text-xs truncate">{service}</p>
                            </div>
                            <span className="text-xs text-gray-300">{status}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="rounded-xl border border-primary-500/20 bg-primary-500/10 p-4">
                      <div className="flex items-start gap-3">
                        <Clock className="w-5 h-5 text-primary-300 mt-0.5" />
                        <div>
                          <p className="text-white font-medium">Fluxo natural</p>
                          <p className="text-sm text-gray-300 mt-1">Cliente escolhe serviço, barbeiro, horário e recebe confirmação automática. O dono acompanha tudo no painel.</p>
                        </div>
                      </div>
                    </div>

                    <button onClick={() => { setShowDemo(false); setShowForm(true); }} className="btn-primary w-full">
                      Quero testar com minha barbearia
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="py-12 px-4 sm:px-6 lg:px-8 border-t border-dark-600">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary-600 to-purple-600 flex items-center justify-center">
                <Scissors className="w-4 h-4 text-white" />
              </div>
              <span className="text-lg font-bold text-white">Barber<span className="text-primary-400">SaaS</span></span>
            </div>
            <p className="text-gray-500 text-sm">
              © 2024 BarberSaaS. Todos os direitos reservados.
            </p>
            <div className="flex items-center gap-6">
              <Link to="/terms" className="text-gray-500 hover:text-gray-300 text-sm">Termos</Link>
              <Link to="/privacy" className="text-gray-500 hover:text-gray-300 text-sm">Privacidade</Link>
              <a href="#" className="text-gray-500 hover:text-gray-300 text-sm">Contato</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
